/** @format */

import {
  ChangeDetectionStrategy,
  Component,
  Signal,
  computed,
  effect,
  inject,
  input,
  signal,
  untracked,
} from '@angular/core';
import { UtilityService, WINDOW } from '@shared/data-access/services';
import {
  availabilityFilterChanged,
  selectFeatureFlags,
  selectFulfillmentFacetCounts,
  selectSelectedFilters,
} from '@plp/data-access/state';
import { selectUserStoreInfo, selectUserZipCode } from '@state/data-access';

import { FULFILLMENT_SECTION_LABELS } from '@plp/data-access/constants';
import { FulfillmentFilterChipComponent } from './fulfillment-filter-chip/fulfillment-filter-chip.component';
import { FulfillmentFilterSelectableComponent } from './fulfillment-filter-selectable/fulfillment-filter-selectable.component';
import type {
  FulfillmentLocationInfo,
  FulfillmentSectionHeaders,
} from '@plp/data-access/interfaces';
import { HeaderStoreSelection } from '@shared/data-access/interfaces';
import { ProductListLayoutService } from '@plp/data-access/services';
import { Store } from '@ngrx/store';

@Component({
  selector: 'plp-components-shipping-filter',
  imports: [FulfillmentFilterSelectableComponent, FulfillmentFilterChipComponent],
  templateUrl: './shipping-options.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ShippingOptionsComponent {
  readonly isMobile = input(false);
  readonly optionVariant = input<'chip' | 'selectable'>('chip');
  readonly disabled = input<boolean>(false);
  readonly mobileSelectionPosition = input<'top' | 'left'>('top');
  readonly selectedOptions = signal<string[]>([]);

  private readonly store = inject(Store);
  private readonly utilityService = inject(UtilityService);
  private readonly productListLayoutService = inject(ProductListLayoutService);
  protected readonly window = inject(WINDOW);

  private readonly featureFlags = this.store.selectSignal(selectFeatureFlags);
  readonly fulfillmentFacets = this.store.selectSignal(selectFulfillmentFacetCounts);
  readonly selectedFilters = this.store.selectSignal(selectSelectedFilters);
  readonly userZipCode: Signal<string> = this.store.selectSignal(selectUserZipCode);
  readonly userStoreInfo: Signal<HeaderStoreSelection | null> =
    this.store.selectSignal(selectUserStoreInfo);

  readonly sameDayDeliveryEnabled = computed(() => this.featureFlags().sameDayDeliveryEnabled);
  readonly fulfillmentFacetsEnabled = computed(() => this.featureFlags().fulfillmentFacetsEnabled);
  readonly exposedFiltersEnabled = computed(() => this.featureFlags().exposedFiltersEnabled);
  readonly moveFulfillmentToFilters = computed(
    () => this.featureFlags().movefulfillmentOptionsToFilters,
  );

  private readonly FILTER_NAME = 'facetStore';

  readonly chipLocationInfo = computed(() => {
    const selected = this.selectedOptions()[0];
    if (!selected) {
      return null;
    }

    return this.buildLocationInfo(selected);
  });

  private readonly buildLocationInfo = (selected: string): FulfillmentLocationInfo | null => {
    const labels = FULFILLMENT_SECTION_LABELS.chip;
    const cta = FULFILLMENT_SECTION_LABELS.ctaPlaceholder;
    const zip = this.userZipCode() || cta.zip;
    const store = this.userStoreInfo()?.name || cta.store;

    switch (selected) {
      case 'SHIP':
        return {
          key: 'SHIP',
          label: labels.SHIP,
          value: zip,
          changeHint: 'ship',
          ariaLabel: 'Change ship to location',
        };
      case 'SDD':
        return {
          key: 'SDD',
          label: labels.SDD,
          value: zip,
          changeHint: 'ship',
          ariaLabel: 'Change delivery location',
        };
      case 'BOPIS':
        return {
          key: 'BOPIS',
          label: labels.BOPIS,
          value: store,
          changeHint: 'pickup',
          ariaLabel: 'Change pickup location',
        };
      case 'ISA':
        return {
          key: 'ISA',
          label: this.moveFulfillmentToFilters() ? 'Shop at' : labels.ISA,
          value: store,
          changeHint: 'pickup',
          ariaLabel: 'Change store',
        };
      default:
        return null;
    }
  };

  readonly selectableSections: Signal<FulfillmentSectionHeaders> = computed(() => {
    const labels = FULFILLMENT_SECTION_LABELS.selectable;
    const cta = FULFILLMENT_SECTION_LABELS.ctaPlaceholder;

    return {
      pickup: {
        label: this.sameDayDeliveryEnabled() ? labels.pickup.sddEnabled : labels.pickup.sddDisabled,
        value: this.userStoreInfo()?.name || cta.store,
      },
      ship: {
        label: labels.ship.label,
        value: this.userZipCode() || cta.zip,
      },
    };
  });

  constructor() {
    effect(() => {
      const options = this.selectedFilters()[this.FILTER_NAME] || [];
      untracked(() => {
        const currentSignalValue = this.selectedOptions();
        const isEqual =
          currentSignalValue.length === options.length &&
          currentSignalValue.every((val, i) => val === options[i]);

        if (!isEqual) {
          this.selectedOptions.set([...options]);
        }
      });
    });
  }

  async setSelectedFilter(filterName: string): Promise<void> {
    if (this.disabled()) {
      return;
    }

    if (this.selectedOptions().includes(filterName)) {
      this.selectedOptions.update((e) => e.filter((f) => f !== filterName));
    } else {
      const canApply = this.canApplyFilterStatus(filterName);

      if (canApply === 'need-store') {
        this.selectUserZipCodeAndStoreInfo('pickup');
        return;
      }

      if (canApply === 'need-zip') {
        this.selectUserZipCodeAndStoreInfo('ship');
        return;
      }

      this.selectedOptions.set([filterName]);
    }

    await this.utilityService.yieldTask(() => {
      this.setSelectedAvailabilityFilter(filterName);
      this.productListLayoutService.scrollToTop();
    });
  }

  openStorePanel(): void {
    if (this.window?.headerFunctions?.storeSelection) {
      this.window.headerFunctions.storeSelection();
    }
  }

  /**
   * @description
   * Gets called on select store or delivery location link clicked on mobile
   */
  selectUserZipCodeAndStoreInfo(hint: 'pickup' | 'ship'): void {
    if (hint === 'ship') {
      this.openZipCodePanel();
    } else {
      this.openStorePanel();
    }
  }

  private setSelectedAvailabilityFilter(filterName: string): void {
    const refinementUpdateObj = this.selectedOptions().includes(filterName)
      ? {
          UpdateType: 'Refinement',
          UpdateValue: {
            RefinementValue: filterName,
            RefinementAttribute: this.FILTER_NAME,
            ProductCountTotal: undefined,
          },
        }
      : undefined;

    queueMicrotask(() => {
      this.store.dispatch(
        availabilityFilterChanged({
          selectedFilters: { facetStore: this.selectedOptions() },
          refinementUpdateObj,
        }),
      );
    });
  }

  private openZipCodePanel(): void {
    if (this.window?.headerFunctions?.sameDayDelivery) {
      this.window.headerFunctions.sameDayDelivery();
    }
  }

  private canApplyFilterStatus(filterName: string): 'need-store' | 'need-zip' | 'ok' {
    if ((filterName === 'BOPIS' || filterName === 'ISA') && !this.userStoreInfo()) {
      return 'need-store';
    }
    if ((filterName === 'SHIP' || filterName === 'SDD') && !this.userZipCode()) {
      return 'need-zip';
    }
    return 'ok';
  }
}
